/**
 * MASAK yaptırım listesi taraması (bkz. backend masak-uyum.md "Yaptırım listesi taraması").
 * Müşteri OFAC SDN, BM, AB ve TR malvarlığı dondurma (MASAK) listelerine karşı ad (+ doğum yılı)
 * benzerliğiyle ve TCKN/VKN'nin listede aynen geçip geçmediğiyle taranır. Enum değerleri JewApi
 * DbEnums ile birebir aynı.
 */

export enum SanctionListSource {
  OfacSdn = 1,
  UnConsolidated = 2,
  EuConsolidated = 3,
  /** Yönetim panelinden elle girilen TR kararları. */
  TurkeyAssetFreeze = 4,
  /** MASAK malvarlığı dondurulanlar listesi (6415 md.6/md.7) — günlük otomatik aktarım. */
  TurkeyMasak = 5,
}

export const SANCTION_LIST_SOURCE_LABELS: Record<number, string> = {
  [SanctionListSource.OfacSdn]: 'OFAC SDN',
  [SanctionListSource.UnConsolidated]: 'BM',
  [SanctionListSource.EuConsolidated]: 'AB',
  [SanctionListSource.TurkeyAssetFreeze]: 'TR (elle girilen)',
  [SanctionListSource.TurkeyMasak]: 'TR MASAK',
};

export enum SanctionEntryType {
  Person = 1,
  Organization = 2,
  Vessel = 3,
  Aircraft = 4,
  Other = 9,
}

export enum SanctionListImportStatus {
  Succeeded = 1,
  Failed = 2,
  Unchanged = 3,
}

export enum SanctionScreeningResult {
  Clean = 1,
  PossibleMatch = 2,
}

export enum SanctionScreeningReviewStatus {
  NotRequired = 0,
  Pending = 1,
  FalsePositive = 2,
  ConfirmedMatch = 3,
}

export const SANCTION_SCREENING_REVIEW_OPTIONS = [
  { value: SanctionScreeningReviewStatus.Pending, label: 'İnceleme bekliyor' },
  { value: SanctionScreeningReviewStatus.FalsePositive, label: 'Yanlış pozitif' },
  { value: SanctionScreeningReviewStatus.ConfirmedMatch, label: 'Gerçek eşleşme' },
];

/** Kimlik tespiti kaydına iliştirilen son tarama özeti. */
export class SanctionScreeningSummary {
  id?: number;
  result?: SanctionScreeningResult;
  reviewStatus?: SanctionScreeningReviewStatus;
  screenedAt?: Date;
  matchCount?: number;
  bestScore?: number;
}

/** Bir olası eşleşmenin tarama anındaki anlık görüntüsü. */
export class SanctionMatch {
  queryName?: string;
  source?: SanctionListSource;
  externalId?: string;
  entryType?: SanctionEntryType;
  primaryName?: string;
  matchedName?: string;
  /** 0-1 arası benzerlik. Kimlik numarası eşleşmesinde 1. */
  score?: number;
  /** Eşleşme müşterinin TCKN/VKN'siyle bulunduysa o numara; ad benzerliğiyle bulunduysa boş. */
  matchedIdentityNumber?: string;
  birthDates?: string;
  nationalities?: string;
  programs?: string;
  remarks?: string;
}

export class SanctionScreening extends SanctionScreeningSummary {
  /** Manuel taramada (müşteri kartı olmadan) boş. */
  customerId?: number | null;
  customerName?: string;
  customerIdentityVerificationId?: number;
  screenedNames?: string;
  screenedBirthYear?: number;
  /** Manuel taramada girilen TCKN. */
  screenedIdentityNumber?: string | null;
  listVersions?: string;
  reviewedAt?: Date;
  reviewerName?: string;
  reviewNote?: string;
  /** Yalnızca tekil kayıt yanıtında dolu — listede boş. */
  matches?: SanctionMatch[];
}

export class SanctionScreeningFilter {
  customerId?: number;
  customerIdentityVerificationId?: number;
  result?: SanctionScreeningResult;
  reviewStatus?: SanctionScreeningReviewStatus;
  startDate?: string;
  endDate?: string;
  search?: string;
  /** true: yalnızca manuel taramalar; false: yalnızca müşteri taramaları. */
  manual?: boolean;
}

/** Müşteri kartı olmadan tarama — TCKN, ad ve soyaddan en az biri. */
export class ManualSanctionScreeningRequest {
  tckn?: string;
  firstName?: string;
  lastName?: string;
}

export class SanctionScreeningReviewRequest {
  reviewStatus?: SanctionScreeningReviewStatus;
  reviewNote?: string;
}

export class SanctionListSourceStatus {
  source?: SanctionListSource;
  label?: string;
  entryCount?: number;
  lastImportAt?: Date;
  publishDate?: Date;
  lastStatus?: SanctionListImportStatus;
  lastError?: string;
}

export class SanctionListStatus {
  /** Tabloda en az bir kayıt var — tarama yapılabilir. */
  ready?: boolean;
  sources?: SanctionListSourceStatus[];
}

/** İnceleme gerekçesinin asgari uzunluğu — backend ScreeningReviewNoteMinLength ile aynı. */
export const SCREENING_REVIEW_NOTE_MIN_LENGTH = 10;

/** Liste rozeti / detay başlığı için etiket + Bootstrap rozet sınıfı. Tarama yoksa "Taranmadı". */
export function screeningBadge(summary?: SanctionScreeningSummary | null): { label: string; cssClass: string } {
  if (!summary) return { label: 'Taranmadı', cssClass: 'bg-light text-muted border' };
  if (summary.result === SanctionScreeningResult.Clean) return { label: 'Temiz', cssClass: 'bg-success' };
  switch (summary.reviewStatus) {
    case SanctionScreeningReviewStatus.FalsePositive:
      return { label: 'Yanlış pozitif', cssClass: 'bg-secondary' };
    case SanctionScreeningReviewStatus.ConfirmedMatch:
      return { label: 'Gerçek eşleşme', cssClass: 'bg-danger' };
    default:
      return { label: 'Olası eşleşme', cssClass: 'bg-warning text-dark' };
  }
}
